import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ShieldCheck, X } from "lucide-react";

const CONSENT_KEY = "encore-cookie-consent";

export type ConsentStatus = "accepted" | "declined" | null;

const safeGetLocalStorage = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSetLocalStorage = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // ignore storage failures (private mode / blocked storage)
  }
};

export const getConsentStatus = (): ConsentStatus => {
  const value = safeGetLocalStorage(CONSENT_KEY);
  return (value === "accepted" || value === "declined") ? value : null;
};

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const status = getConsentStatus();
    if (!status) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    safeSetLocalStorage(CONSENT_KEY, "accepted");
    setVisible(false);
  };

  const handleDecline = () => {
    safeSetLocalStorage(CONSENT_KEY, "declined");
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-md z-[100]"
        >
          <div className="rounded-2xl border border-border bg-card p-5 shadow-xl backdrop-blur-sm">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                <ShieldCheck className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display font-semibold text-foreground text-sm">We use cookies 🍪</h3>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  We use cookies and similar technologies to analyze site usage and improve your experience. By accepting, you agree to our use of analytics cookies.
                </p>
                <div className="flex items-center gap-2 mt-4">
                  <Button
                    size="sm"
                    className="font-display gradient-hero border-0 h-8 px-4 text-xs"
                    onClick={handleAccept}
                  >
                    Accept All
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="font-display border-border h-8 px-4 text-xs"
                    onClick={handleDecline}
                  >
                    Decline
                  </Button>
                </div>
              </div>
              <button
                onClick={handleDecline}
                aria-label="Decline cookies"
                className="shrink-0 rounded-lg p-1 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
